import {
  type PortableTextTypeComponentOptions,
  toHTML,
} from '@portabletext/to-html'
import h from 'hyperscript'
import { useContext, useState, useEffect, useMemo } from 'react'

import {
  SanityComplexPortableText,
  SanityQuote,
  SanitySimplePortableText,
} from '@data/sanity/queries/types/content'
import { SanityImageFragment } from '@data/sanity/queries/types/image'
import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPrice, getFirstValue } from '@lib/helpers'
import { getSanityImageUrl } from '@lib/image'
import { getProductGalleryPhotos } from '@lib/product'
import {
  ReviewOptions,
  ReviewProvider,
  getProductReviews,
  ProductReview,
  getProductReviewSummary,
} from '@lib/review'
import { getPageUrl, PageType } from './routes'
import { ShopContext } from '@lib/shop-context'
import { ProductVariantSelectionContext } from './product-variant-selection-context'

type QuoteValue = Pick<SanityQuote, 'author' | 'alignment' | 'text'>

interface BrandSchema {
  '@type': 'Brand'
  name: string
}

interface OfferSchema {
  '@type': 'Offer'
  url: string
  availability: string
  price?: string
  priceCurrency?: string
  priceValidUntil?: string
}

interface ReviewSchema {
  '@type': 'Review'
  reviewRating: {
    '@type': 'Rating'
    ratingValue?: number
  }
  author: {
    '@type': 'Person'
    name?: string
  }
  reviewBody?: string
}

interface AggregateRatingSchema {
  '@type': 'AggregateRating'
  ratingCount: string
  ratingValue: string
}

interface ProductSchema {
  '@context': 'http://schema.org'
  '@type': 'Product'
  name: string
  description?: string
  sku?: string
  gtin?: string
  brand: BrandSchema
  image?: string
  offers?: OfferSchema
  review?: ReviewSchema[]
  aggregateRating?: AggregateRatingSchema
}

interface Rating {
  reviews?: ReviewSchema[]
  aggregateRating?: AggregateRatingSchema
}

const getPortableTextHtml = (
  portableText?: SanityComplexPortableText | SanitySimplePortableText,
) => {
  if (!portableText) {
    return ''
  }

  return toHTML(
    portableText.filter(({ _type }) =>
      ['block', 'quote', 'figure'].includes(_type),
    ),
    {
      components: {
        types: {
          quote: ({ value }: PortableTextTypeComponentOptions<QuoteValue>) => {
            return h(
              'figure',
              {},
              h('blockquote', value.text),
              h('figcaption', value.author),
            ).outerHTML
          },
          figure: ({
            value,
          }: PortableTextTypeComponentOptions<SanityImageFragment>) => {
            const imageUrl = getSanityImageUrl(value, {
              width: value.dimensions?.width,
              height: value.dimensions?.height,
              fitMax: true,
              autoFormat: true,
            })

            return h('img', {
              src: imageUrl,
              alt: value.alt,
            }).outerHTML
          },
        },
      },
    },
  )
}

/**
 * Gets aggregate rating schema.
 */
export const getAggregateRatingSchema = (
  ratingCount?: number,
  ratingValue?: number,
): AggregateRatingSchema | undefined => {
  if (!ratingCount || !ratingValue) {
    return
  }
  return {
    '@type': 'AggregateRating',
    ratingCount: `${ratingCount}`,
    ratingValue: `${ratingValue}`,
  }
}

/**
 * Gets reviews schema from given reviews.
 */
export const getReviewsSchema = (reviews: ProductReview[]) => {
  if (reviews.length === 0) {
    return
  }

  return reviews.map((review) => {
    const reviewSchema: ReviewSchema = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.reviewRating,
      },
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewBody: review.reviewMessage,
    }

    return reviewSchema
  })
}

/**
 * Returns product schema hook.
 */
export const useProductSchema = (
  site: SanitySiteFragment,
  product: SanityProductFragment,
) => {
  const { currencyCode, taxRate, shopifyDomain } = useContext(ShopContext)
  const { selectedVariant } = useContext(ProductVariantSelectionContext)

  const [ratingSchema, setRatingSchema] = useState<Rating>()

  // Load reviews and aggregate rating
  useEffect(() => {
    const loadRatingSchema = async () => {
      const options: ReviewOptions = {
        provider: ReviewProvider.STAMPED,
        apiKey: site.settings.stampedApiKey,
      }

      const reviews = await getProductReviews(
        shopifyDomain,
        product.id,
        options,
      )
      const newReviewsSchema = getReviewsSchema(reviews)

      const productReviewSummary = await getProductReviewSummary(
        shopifyDomain,
        product.id,
        options,
      )
      const newAggregateRating = getAggregateRatingSchema(
        productReviewSummary.ratingCount,
        productReviewSummary.ratingValue,
      )

      setRatingSchema({
        reviews: newReviewsSchema,
        aggregateRating: newAggregateRating,
      })
    }

    loadRatingSchema()
  }, [product.id, shopifyDomain, site.settings.stampedApiKey])

  const productSchema = useMemo(() => {
    // Wait for Shop currency to load
    if (!currencyCode) {
      return
    }

    const description = getPortableTextHtml(product.description)
    const isInStock = selectedVariant?.inStock ?? product.inStock
    const price = getPrice(
      selectedVariant?.price ?? product.price,
      taxRate,
      false,
      '',
    )
    const comparePrice = getPrice(
      selectedVariant?.comparePrice ?? product.comparePrice,
      taxRate,
      false,
      '',
    )
    const domain = site.settings.siteURL.replace(/\/+$/g, '')
    const productUrl = getPageUrl(PageType.PRODUCT, product.slug, {
      parameters: { selectedVariant: `${selectedVariant?.id}` },
    })
    const nextYearIsoDate = new Date(
      new Date().setFullYear(new Date().getFullYear() + 1),
    )
      .toISOString()
      .split('T')[0]

    const getImage = () => {
      if (!product.photos.main || !selectedVariant) {
        return
      }

      const photos = getProductGalleryPhotos(
        product.photos.main,
        selectedVariant,
      )

      if (!photos) {
        return
      }

      return getSanityImageUrl(photos[0], {
        width: 800,
        fitMax: true,
        autoFormat: true,
      })
    }

    const newProductSchema: ProductSchema = {
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: product.title,
      description,
      sku: getFirstValue([selectedVariant?.sku, product.sku]),
      gtin: getFirstValue([selectedVariant?.barcode, product.barcode]),
      brand: {
        '@type': 'Brand',
        name: site.seo?.siteTitle ?? '',
      },
      image: getImage(),
      offers: {
        '@type': 'Offer',
        availability: `http://schema.org/${isInStock ? 'InStock' : 'SoldOut'}`,
        price: comparePrice && Number(comparePrice) > 0 ? comparePrice : price,
        priceCurrency: currencyCode,
        url: `${domain}${productUrl}`,
        priceValidUntil: nextYearIsoDate,
      },
      review: ratingSchema?.reviews,
      aggregateRating: ratingSchema?.aggregateRating,
    }

    return newProductSchema
  }, [
    currencyCode,
    product,
    ratingSchema,
    site.seo?.siteTitle,
    site.settings.siteURL,
    selectedVariant,
    taxRate,
  ])

  return JSON.stringify(productSchema)
}
